import { inject, Injectable } from '@angular/core';
import {
  API_SERVICES_CONFIG,
  CountryRiskIndex,
  CountryRiskIndexIndicatorProperties,
  CountryRiskIndexNarrative,
  DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  HttpGETCustomOptions,
  IndexWithMaplecroftRiskViews,
  NonPaginatedResourceConfig,
  PaginatedResourceConfig,
  PortalHttpClient,
  QueryParams,
  RiskIndexNarrativePermission,
} from '@grid-ui/common';
import { PermissionsService } from '@grid-ui/permissions';
import { combineLatest, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  ApiCountryRiskIndexIndicator,
  ApiCountryRiskIndexIndicatorsList,
  ApiCountryRiskIndexNarrative,
  ApiCountryRiskIndicesCollection,
  CountryRiskIndexQueryParams,
} from './models';

// TODO: Implement caching once ETags are implemented server-side

/**
 * Service for accessing the Country Risk Indices API
 */
@Injectable()
export class CountryRiskIndicesService {
  private readonly http = inject(PortalHttpClient);
  private readonly permissionsService = inject(PermissionsService);

  private readonly indicesResourcesConfig: PaginatedResourceConfig = API_SERVICES_CONFIG.v3.countryRisk.indices._configuration;
  private readonly indexResourcesConfig: NonPaginatedResourceConfig = API_SERVICES_CONFIG.v3.countryRisk.indices.index._configuration;
  private readonly indexNarrativeResourcesConfig: NonPaginatedResourceConfig =
    API_SERVICES_CONFIG.v3.countryRisk.indices.index.narrative._configuration;
  private readonly indexDefinitionResourcesConfig: NonPaginatedResourceConfig =
    API_SERVICES_CONFIG.v3.countryRisk.indices.index.definition._configuration;
  private readonly indexViewsResourcesConfig: NonPaginatedResourceConfig = API_SERVICES_CONFIG.feApi.indexViews._configuration;

  // TODO: Support arguments to match API parameters (name, theme, updatedSince, configuration)
  /**
   * Get a list of Country Risk Indices which are within the users entitlement.
   *
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getRiskIndices(
    riskIndexQueryParams?: CountryRiskIndexQueryParams | null,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
    defaultPageSize = 600,
  ): Observable<CountryRiskIndex[]> {
    // TODO: Implement caching once ETags are supported server-side

    const queryParams: QueryParams = { page_size: defaultPageSize, ...(riskIndexQueryParams || {}) };

    return combineLatest([
      this.http
        .get<ApiCountryRiskIndicesCollection>(this.indicesResourcesConfig, {
          ...options,
          queryParams,
        })
        .pipe(map(({ results }) => results)),
      from(this.permissionsService.userPermissions).pipe(map((permissions) => permissions['country-risk']?.eti)),
    ]).pipe(map(([indices, eti]) => indices.map((index) => ({ ...index, eti_subscribed: !!eti?.includes(index.id) }))));
  }

  /**
   * Get a list of all Country Risk Indices and maplecroft risk views they are contained within.
   */
  public getRiskIndicesWithMaplecroftRiskViews() {
    return this.http.get<IndexWithMaplecroftRiskViews[]>(this.indexViewsResourcesConfig);
  }

  /**
   * Get the risk index methodology narrative for the specified risk index.
   *
   * @param id Unique ID of risk index
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getRiskIndexNarrative(
    id: string,
    permissions: RiskIndexNarrativePermission = RiskIndexNarrativePermission.countryRisk,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<CountryRiskIndexNarrative> {
    return from(this.permissionsService.userPermissions).pipe(
      switchMap((userPermissions) =>
        userPermissions['country-risk']?.index.includes(id)
          ? this.http.get<ApiCountryRiskIndexNarrative>(this.indexNarrativeResourcesConfig, {
              ...options,
              pathParams: { id },
              queryParams: { permissions },
            })
          : this.http.get<ApiCountryRiskIndexNarrative>(this.indexDefinitionResourcesConfig, {
              ...options,
              pathParams: { id },
              queryParams: { permissions },
            }),
      ),
    );
  }

  /**
   * Get a list of indicator properties for all descendent indicator groups and
   * indicators of the specified risk index.
   *
   * @param id Risk index ID
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getRiskIndexIndicatorProperties(
    id: string,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<Map<string, CountryRiskIndexIndicatorProperties>> {
    const edition = 'indicators';
    return this.http
      .get<ApiCountryRiskIndexIndicatorsList>(this.indexResourcesConfig, {
        ...options,
        pathParams: { id, edition },
      })
      .pipe(map((list) => this.flattenRiskIndexIndicatorPropertiesList(list)));
  }

  /**
   * Create a map from indicator/indicator group ID to indicator/indicator group properties
   * from the specified nested indicator subtree properties list.
   *
   * @param list A nested list of indicator/indicator group properties or a given risk index.
   */
  private flattenRiskIndexIndicatorPropertiesList(
    list: ApiCountryRiskIndexIndicatorsList,
  ): Map<string, CountryRiskIndexIndicatorProperties> {
    const flattenedMap = new Map<string, CountryRiskIndexIndicatorProperties>();
    return this.addChildIndicatorProperties(list.children, flattenedMap);
  }

  /**
   * Recursively add the indicator/indicator group children to the specified
   * map and return an updated map.
   *
   * @param children An array of indicator/indicator group children properties
   * @param flattenedMap The map to be added to.
   */
  private addChildIndicatorProperties(
    children: ApiCountryRiskIndexIndicator[],
    flattenedMap: Map<string, CountryRiskIndexIndicatorProperties>,
  ): Map<string, CountryRiskIndexIndicatorProperties> {
    children.forEach((child) => {
      flattenedMap.set(child.id, child);
      if (child.children.length) {
        flattenedMap = this.addChildIndicatorProperties(child.children, flattenedMap);
      }
    });
    return flattenedMap;
  }
}
